import "./AutosuggestInput.scss";

import React from "react";

import * as PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router";
import ReactAutoSuggest from "react-autosuggest";
import { FormGroup } from "react-bootstrap";
import { connect } from "shared/src/stores/storeConnect";
import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";
import Autosuggestion from "./Autosuggestion";
import SearchAction from "../../actions/SearchAction";
import MobileNavigationActions from "../../actions/MobileNavigationActions";
import {
    computeHighlighting,
    findRecentSearchSuggestions,
    findSuggestions,
    normalizeQuery,
    formatHighlighting,
    wrapFavourites,
    wrapValues
} from "../../utils/AutosuggestUtil";
import { loadRecentSearches } from "../articles/shared/RecentSearches";
import CustomerSettingsStore from "../../stores/settings/CustomerSettingsStore";
import BundleImage from "../articles/shared/article-badges/bundles/BundleImage";
import { AddToBasketControl } from "../articles/shared/AddToBasketControl";
import { CartStore } from "../../stores/CartStore";
import { isTier12Customer } from "shared/src/stores/SessionStore";

import {
    trackSelectedSearchSuggestion,
    trackSelectedArticleSuggestion,
    trackSuggestionIfChanged,
    trackSelectedLinkSuggestion,
    trackSelectedContentSuggestion
} from "../../utils/SearchTrackingUtil";

import {
    ArticleSuggestions,
    SuggestionWithProperties,
    RecentSearchesSuggestion
} from "../../datatypes/ArticleSuggestions";
import ArticleSuggestionStore, { FavouriteData } from "../../stores/ArticleSuggestionStore";
import RouterUtil from "../../utils/RouterUtil";
import { upperFirst } from "../../utils/stringUtil";
import CountryUtil from "shared/src/uidispatcherapi/CountryUtil";
import { isShowAddToCartButtonInsideSuggestionsActive, isSuggestContentEnabled } from "../../searchdiscover/toggles";
import ArticleStore from "../../stores/ArticleStore";
import { FetchedArticle } from "shared/src/stores/ArticleStore";
import CustomerStore from "../../stores/CustomerStore";
import { getBettyBundleNumberFromBettyBundleId } from "shared/src/utils/util";
import { createEvaluateArticlePriceAndPromotionInfo, getCategoryPath, getPdpLink } from "shared/src/utils/articleUtil";
import { PageComponents } from "shared/src/tracking/PageComponents";
import { PageType } from "shared/src/types/PageType";
import { Bundle } from "shared/src/types/articlesearch/Article";
import { canNotBeAddedToBasket } from "shared/src/utils/BasketUtil";
import ErrorBoundary from "shared/src/components/ErrorBoundary";
import { debounce } from "shared/src/utils/util";

type Icon = {
    className: string;
    position?: "front" | "back";
};

export type Suggestion =
    | SuggestionWithProperties
    | {
          type: "FAVOURITE";
          data: FavouriteData;
      }
    | {
          type: "LINK";
          message: string;
          path: string;
          icon?: Icon;
      }
    | RecentSearchesSuggestion;

type Section = {
    title?: string;
    suggestions: Array<Suggestion>;
};

const LATEST_ORDER_DETAIL_PAGE_LINK = "/portal/my-orders/latest-order";
const MY_LISTS_PAGE_LINK = "/listOverview";

const debounceFetchSuggestions = debounce(SearchAction.fetchSuggestions, 200);
const debounceFetchFavourites = debounce(SearchAction.fetchFavourites, 200);

type Props = {
    inputProps: any;
    renderInputComponent: Function;
    value: string;
    onQuery: Function;
    onSubmit: Function;
    shoppingCartItemCount: number;
    suggestionsMap:
        | {
              [x: string]: ArticleSuggestions;
          }
        | undefined
        | null;
    favouritesMap:
        | {
              [x: string]: Array<string>;
          }
        | undefined
        | null;
    favouriteDataMap: {
        [x: string]: FavouriteData;
    };
    isMobile: boolean;
    loggedIn: boolean;
    isFsdCustomer: boolean;
    deliveryStores: Array<string>;
    cartEnabled: boolean;
    selectedStoreId: string;
};

interface State {
    isDesktop: boolean;
    inputProps: any;
}

// NOTE: JUST EXPORT FOR TESTING PURPOSE
export class AutosuggestInput extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        const inputProps = this.normalizeInputValue(props.inputProps);

        this.state = {
            isDesktop: window.innerWidth >= 1024,
            inputProps
        };
        this.handleResize = this.handleResize.bind(this);
    }

    handleResize = () => {
        this.setState({ isDesktop: window.innerWidth >= 1024 });
    };

    normalizeInputValue = (inputProps: { value: string | string[]; placeholder: string }) => {
        return {
            ...inputProps,
            value: inputProps.value
        };
    };

    componentDidMount() {
        window.addEventListener("resize", this.handleResize);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    static contextTypes = {
        router: PropTypes.object.isRequired,
        intl: PropTypes.object.isRequired
    };

    loadSuggestions = (query: { value: string | undefined | null }) => {
        const value = query.value;
        if (value) {
            debounceFetchSuggestions(value);
            debounceFetchFavourites(value);
        }
    };

    renderSectionTitle = (section: Section) => (section.title ? <FormattedMessage id={section.title} /> : null);

    renderQuerySuggestionLabel = (suggestion: SuggestionWithProperties) => {
        if (suggestion.properties && suggestion.properties.categoryParentName) {
            if (suggestion.properties.isCategory) {
                return suggestion.properties.categoryParentName + " / " + suggestion.value;
            } else if (CountryUtil.getOptionalConfig()?.showScopedSearchtermSuggestions) {
                // MSD-1838: So called 'category scoped searchterm suggestions' that lead the
                // user into a category filtered by a certain searchterm suggestion.
                return this.context.intl.formatMessage(
                    { id: "SD_SCOPED_SUGGESTION_LABEL" },
                    { query: suggestion.value, category: suggestion.properties.categoryName }
                );
            }
        }
        return suggestion.value;
    };

    renderSuggestion = (suggestion: Suggestion) => {
        // only here, we can be sure the suggestions are actually being shown.
        // this'll get called once for each suggestion, but we deduplicate it anyway
        this.trackSuggestionsShown();

        if (suggestion.type === "LINK") {
            const icon = suggestion.icon;
            return (
                <Link to={suggestion.path}>
                    {icon && icon.position === "front" ? (
                        <span>
                            <i className={icon.className} />{" "}
                        </span>
                    ) : null}
                    <FormattedMessage id={suggestion.message} />
                    {icon && icon.position === "back" ? <i className={"text-align-right " + icon.className} /> : null}
                </Link>
            );
        }
        if (suggestion.type === "RECENT_SEARCH") {
            return (
                <Autosuggestion
                    value={this.props.value}
                    suggestion={suggestion.recentSearches.query}
                    icon={suggestion.icon.className}
                    properties={null}
                    isRecentSearch
                />
            );
        }
        if (suggestion.type === "FAVOURITE") {
            const firstValue = this.props.value;
            const highlighting =
                firstValue && suggestion.data.description
                    ? computeHighlighting(firstValue, suggestion.data.description, false)
                    : null;
            const bettyBundleId = suggestion?.data?.bettyBundleId || "";
            const bundleNumber = bettyBundleId ? getBettyBundleNumberFromBettyBundleId(bettyBundleId) : "";
            const suggestionArticleData = ArticleStore.getOneArticleDataWithBundleId(bettyBundleId);
            const bundle: FetchedArticle["bundles"] | null =
                (suggestionArticleData &&
                !("state" in suggestionArticleData) &&
                suggestionArticleData?.bundles &&
                bundleNumber
                    ? suggestionArticleData.bundles[bundleNumber]
                    : null) || null;
            const storeId = this.props.selectedStoreId;
            const isUserLoggedInAndIsFSDCustomerOnDesktop =
                !this.props.isMobile && this.state.isDesktop && this.props.loggedIn && this.props.isFsdCustomer;
            const showAddToCartInSuggestions =
                isShowAddToCartButtonInsideSuggestionsActive() && isUserLoggedInAndIsFSDCustomerOnDesktop;
            const isNonExistentBundle = !bundle;
            const amountInBasket = CartStore.getItemAmount(bettyBundleId);
            const shoppingCartPosition = CartStore.getCartItemTotalNetPrice(bettyBundleId);
            const sanitizedCategoryPath = getCategoryPath(bundle as Bundle);
            const priceAndPromotionInfo = createEvaluateArticlePriceAndPromotionInfo(
                suggestionArticleData as FetchedArticle,
                {
                    selectedBundleId: bundleNumber,
                    originalBundleId: null,
                    bettyOfferId: null
                },
                storeId,
                {
                    [bettyBundleId]: amountInBasket
                },
                shoppingCartPosition
            );

            const stores = bundle?.stores;
            const selectedStore = (stores && storeId ? stores[storeId] : null) || null;
            const selectedDeliveryMode = selectedStore?.sellingPriceInfo?.selectedDeliveryMode || null;
            const isDisabledAddToCartButton = canNotBeAddedToBasket(
                false,
                isNonExistentBundle,
                this.props.cartEnabled,
                {
                    customerBuyable: selectedStore?.customerBuyable,
                    employeeBuyable: selectedStore?.employeeBuyable,
                    delivery: selectedDeliveryMode
                }
            );
            const variantName =
                (suggestionArticleData && "description" in suggestionArticleData
                    ? suggestionArticleData?.description
                    : null) || "";

            return (
                <div className={"list-item-favourite"}>
                    <BundleImage
                        src={suggestion.data.imageUrls}
                        description={suggestion.data.description}
                        onSuccessfulLoad={() => {}}
                        defaultWidth={50}
                        defaultHeight={50}
                        breakPointConfigs={[{ imageWidth: 50, imageHeight: 50 }]}
                        imgClassName="article-suggestion-image"
                    />
                    {highlighting ? (
                        <div className={"list-item-autosuggestion-text"}>{formatHighlighting(highlighting)}</div>
                    ) : (
                        <div className={"article-suggestion-description"}>{suggestion.data.description}</div>
                    )}
                    {showAddToCartInSuggestions && (
                        <div className="add-to-cart-button-section">
                            <ErrorBoundary>
                                <AddToBasketControl
                                    renderMinimally
                                    idForBasket={suggestion?.data?.bettyBundleId}
                                    amountInBasket={amountInBasket}
                                    priceInBasket={shoppingCartPosition}
                                    isWeightArticle={bundle?.isWeightArticle === "WEIGHT"}
                                    weightPerPiece={bundle?.weightPerPiece}
                                    netPieceWeight={bundle?.contentData?.netPieceWeight?.value}
                                    dnrInfo={selectedStore?.sellingPriceInfo?.dnrInfo}
                                    onlyEmployeeBuyable={false}
                                    deliveryMode={selectedDeliveryMode}
                                    disabled={isDisabledAddToCartButton}
                                    loggedIn={this.props.loggedIn}
                                    isTier12Customer={isTier12Customer()}
                                    tabIndex={undefined}
                                    minOrderQuantity={bundle?.minOrderQuantity}
                                    priceAndPromotionInfo={priceAndPromotionInfo}
                                    trackingData={{
                                        originEventId: "",
                                        inContainer: false,
                                        onList: false,
                                        recentlyOrdered: false,
                                        recentlyOrderedDateString: null,
                                        promotion: null,
                                        pageType: "unknown" as PageType,
                                        pageComponent: PageComponents.SUGGESTIONS,
                                        zeroResultType: null,
                                        bettyBundleId: bundle?.bundleId?.bettyBundleId,
                                        trackingId: bundle?.trackingInfo?.trackingId,
                                        description: bundle?.description,
                                        marketplace: false,
                                        offerId: null,
                                        articleFinalNetValue: priceAndPromotionInfo?.unitNet ?? null,
                                        currency: selectedStore?.sellingPriceInfo?.currency,
                                        brandName: bundle?.brandName,
                                        variantName: variantName,
                                        strokePrice: priceAndPromotionInfo?.strikeThroughNet ?? null,
                                        loggedIn: this.props.loggedIn,
                                        categoryPath: sanitizedCategoryPath,
                                        weight: bundle?.contentData?.netPieceWeight?.value || null,
                                        listId: null,
                                        listPosition: null,
                                        availability: bundle?.availability,
                                        unitSize: null,
                                        imageSrc: bundle?.imageUrl,
                                        ownBrand: bundle?.ownBrand,
                                        url: getPdpLink(bundle),
                                        subsysNumber: bundle?.refBundleIds?.[0]?.subsystemArticleNumber ?? "",
                                        mgbNumber: bundle?.refBundleIds?.[0]?.mgbBundleNumber ?? ""
                                    }}
                                />
                            </ErrorBoundary>
                        </div>
                    )}
                </div>
            );
        }
        return (
            <Autosuggestion
                value={this.props.value}
                suggestion={this.renderQuerySuggestionLabel(suggestion)}
                icon={suggestion.icon.className}
                properties={suggestion.properties}
                isRecentSearch={false}
            />
        );
    };

    renderSuggestionSafely = (suggestion: Suggestion) => {
        return <ErrorBoundary>{this.renderSuggestion(suggestion)}</ErrorBoundary>;
    };

    getSuggestionValue = (suggestion: Suggestion) => {
        if (suggestion.type === "SUGGESTION") {
            return suggestion.value;
        } else if (suggestion.type === "RECENT_SEARCH") {
            return suggestion.recentSearches.query;
        }
        return "";
    };

    onSubmit = (e: React.SyntheticEvent<any>) => {
        if (e) {
            e.preventDefault();
        }
        debounceFetchSuggestions.cancel();
        debounceFetchFavourites.cancel();
        SearchAction.cancelSuggestions();
        this.props.onSubmit(e);
    };

    suggestionSelected = (
        e: React.SyntheticEvent<any>,
        selection: {
            suggestion: Suggestion;
            method: string;
            suggestionValue: string;
            suggestionIndex: number;
            sectionIndex: number;
        }
    ) => {
        if (e) {
            e.preventDefault();
        }

        const { method, suggestion, suggestionIndex, sectionIndex } = selection;
        const fsd = CustomerSettingsStore.isFsdFilterActive();
        const indexWithinGroup = suggestionIndex;
        const originalQuery = this.props.value;
        const totalIndex = this.determineTotalSuggestionIndex(sectionIndex, suggestionIndex);

        if (suggestion && suggestion.type === "LINK") {
            this.context.router.push(suggestion.path);
            MobileNavigationActions.CloseMobileMenu();
            trackSelectedLinkSuggestion(suggestion.path, totalIndex, indexWithinGroup, originalQuery, method, fsd);
        } else if (suggestion && suggestion.type === "FAVOURITE") {
            if (!(e && e.target && ((e.target as any).matches("button") || (e.target as any).matches("i")))) {
                SearchAction.cancelSuggestions();
                trackSelectedArticleSuggestion(
                    suggestion.data.bettyVariantId,
                    totalIndex,
                    indexWithinGroup,
                    originalQuery,
                    method,
                    fsd,
                    suggestion.data.bettyBundleId
                );
                this.context.router.push(suggestion.data.location);
                MobileNavigationActions.CloseMobileMenu();
            }
        } else {
            const suggestionValue = selection.suggestionValue.trim();
            const suggestContent = isSuggestContentEnabled();
            if (
                suggestion.type === "SUGGESTION" &&
                suggestion.properties &&
                suggestion.properties.isCategory &&
                suggestion.properties.categoryPath
            ) {
                trackSelectedSearchSuggestion(
                    suggestionValue,
                    totalIndex,
                    indexWithinGroup,
                    originalQuery,
                    method,
                    fsd,
                    "category"
                );
                RouterUtil.redirectToCategory(suggestion.properties.categoryPath, this.context.router);
            } else if (
                suggestion.type === "SUGGESTION" &&
                suggestion.properties &&
                !suggestion.properties.isCategory &&
                suggestion.properties.categoryPath &&
                CountryUtil.getOptionalConfig()?.showScopedSearchtermSuggestions
            ) {
                trackSelectedSearchSuggestion(
                    suggestionValue,
                    totalIndex,
                    indexWithinGroup,
                    originalQuery,
                    method,
                    fsd,
                    "scoped-term"
                );
                RouterUtil.redirectToCategory(
                    suggestion.properties.categoryPath,
                    this.context.router,
                    suggestion.value
                );
            } else if (suggestion.type === "SUGGESTION" && suggestion.properties && suggestion.properties.isBrand) {
                trackSelectedSearchSuggestion(
                    suggestionValue,
                    totalIndex,
                    indexWithinGroup,
                    originalQuery,
                    method,
                    fsd,
                    "brand"
                );
                const suggestionString = suggestion.originalValue ? suggestion.originalValue : suggestionValue;
                this.props.onQuery(suggestionString, "brand:" + suggestionString);
            } else if (
                suggestion.type === "SUGGESTION" &&
                suggestContent &&
                suggestion.properties &&
                suggestion.properties.contentUrl
            ) {
                trackSelectedContentSuggestion(
                    suggestion.properties.contentUrl,
                    totalIndex,
                    indexWithinGroup,
                    originalQuery,
                    method,
                    fsd
                );
                window.location.href = suggestion.properties.contentUrl;
                MobileNavigationActions.CloseMobileMenu();
            } else {
                trackSelectedSearchSuggestion(
                    suggestionValue,
                    totalIndex,
                    indexWithinGroup,
                    originalQuery,
                    method,
                    fsd,
                    "term"
                );
                this.props.onQuery(suggestionValue, null);
            }
        }
    };

    trackSuggestionsShown() {
        const currentSuggestions = findSuggestions(
            normalizeQuery(this.props.value),
            this.props.suggestionsMap || {}
        ) || {
            suggestions: []
        };
        const currentFavourites = findSuggestions(this.props.value, this.props.favouritesMap || {}) || [];
        const currentLinkSuggestions = this.getLinkSuggestions().map((suggestion) => suggestion.message);

        trackSuggestionIfChanged(currentSuggestions, currentFavourites, currentLinkSuggestions);
    }

    determineTotalSuggestionIndex(sectionIndex: number, suggestionIndex: number): number {
        const sections = this.getSuggestionSections();
        let index = 0;
        for (let i = 0; i < sectionIndex; ++i) {
            if (sections[i]) {
                index += sections[i].suggestions.length;
            }
        }
        index += suggestionIndex;
        return index;
    }

    getLinkSuggestions() {
        if (this.props.loggedIn && !this.props.value) {
            return [
                {
                    type: "LINK",
                    message: "AUTOSUGGEST_GO_TO_LATEST_ORDER_LINK",
                    path: LATEST_ORDER_DETAIL_PAGE_LINK,
                    icon: { position: "back", className: "icon-small-link" }
                },
                {
                    type: "LINK",
                    message: "AUTOSUGGEST_GO_TO_MY_LISTS_LINK",
                    path: MY_LISTS_PAGE_LINK,
                    icon: { position: "back", className: "icon-small-link" }
                }
            ];
        } else {
            return [];
        }
    }

    addSuggestionsInSeparateSections(
        matchingRecentSearchSuggestions: { suggestions: RecentSearchesSuggestion[] } | null,
        suggestionExists: any[],
        suggestions: any[]
    ) {
        const suggestionsLowerCase = suggestionExists
            .filter(
                (a) =>
                    !(
                        (a.properties && a.properties.isBrand) ||
                        (a.properties && a.properties.isCategory && a.properties.categoryPath !== null)
                    )
            )
            .map((a) => ({ ...a, value: a.value }));
        const suggestionResults = suggestionsLowerCase.filter((e) => !e.existingSuggestion);
        if (matchingRecentSearchSuggestions) {
            // Include "Recently searched" suggestions in general suggestions section
            const adjustedMatchingRecentSearchSuggestions = {
                ...matchingRecentSearchSuggestions,
                title: "SD_STANDARD_SUGGESTION_TEXT"
            };
            suggestions.push(adjustedMatchingRecentSearchSuggestions);

            if (suggestionResults.length > 0) {
                suggestions.push({
                    suggestions: suggestionResults
                });
            }
        } else {
            // There are no "Recently searched" suggestions to be included in the general suggestions section
            if (suggestionResults.length > 0) {
                suggestions.push({
                    title: "SD_STANDARD_SUGGESTION_TEXT",
                    suggestions: suggestionResults
                });
            }
        }

        const brandSuggestionsUpperCase = suggestionExists
            .filter((a) => a.properties && a.properties.isBrand)
            .map((a) => ({ ...a, value: upperFirst(a.value) }));
        const brandResults = brandSuggestionsUpperCase.filter(
            (e) => !e.existingSuggestion || (e.existingSuggestion && e.properties && e.properties.isBrand)
        );
        if (brandResults.length > 0) {
            suggestions.push({
                title: "SD_BRAND_SUGGESTION_TEXT",
                suggestions: brandResults
            });
        }

        const categorySuggestionsUpperCase = suggestionExists
            .filter((a) => a.properties && a.properties.isCategory && a.properties.categoryPath !== null)
            .map((a) => ({ ...a, value: upperFirst(a.value) }));
        const categoryResults = categorySuggestionsUpperCase.filter(
            (e) =>
                !e.existingSuggestion ||
                (e.existingSuggestion && e.properties && e.properties.isCategory && e.properties.categoryPath !== null)
        );
        if (categoryResults.length > 0) {
            suggestions.push({
                title: "SD_CATEGORY_SUGGESTION_TEXT",
                suggestions: categoryResults
            });
        }
    }

    addSuggestionsInOverallSection(
        matchingRecentSearchSuggestions: { suggestions: RecentSearchesSuggestion[] } | null,
        suggestionExists: any[],
        suggestions: any[]
    ) {
        if (matchingRecentSearchSuggestions) {
            suggestions.push(matchingRecentSearchSuggestions);
        }
        const suggestionsUpperCase = suggestionExists.map((a) =>
            (a.properties && a.properties.isBrand) ||
            (a.properties && a.properties.isCategory && a.properties.categoryPath !== null)
                ? { ...a, value: upperFirst(a.value) }
                : { ...a, value: a.value }
        );
        const results = suggestionsUpperCase.filter(
            (e) =>
                !e.existingSuggestion ||
                (e.existingSuggestion &&
                    e.properties &&
                    e.properties.isCategory &&
                    e.properties.categoryPath !== null) ||
                (e.existingSuggestion && e.properties && e.properties.isBrand)
        );
        suggestions.push({
            suggestions: results
        });
    }

    constructUserInputDependentSections(suggestions: any[]) {
        let matchingRecentSearchSuggestions = null;
        const searchSuggestions = findRecentSearchSuggestions(this.props.value);
        if (searchSuggestions.length > 0) {
            matchingRecentSearchSuggestions = {
                suggestions: searchSuggestions
            };
        }
        const currentSuggestions = findSuggestions(
            normalizeQuery(this.props.value),
            this.props.suggestionsMap || {}
        ) || {
            suggestions: []
        };
        const suggestionValues = wrapValues(
            currentSuggestions.suggestions,
            currentSuggestions.suggestionProperties
        ).map((a) => ({ ...a, value: a.value.toLowerCase() }));
        const recentSearchValues = findRecentSearchSuggestions(this.props.value).map((a) => a.recentSearches.query);
        const suggestionExists = suggestionValues.map((x) => ({
            ...x,
            existingSuggestion: recentSearchValues.includes(x.value)
        }));

        if (CountryUtil.getOptionalConfig()?.separateSuggestionTypes) {
            // This is the new design where suggestions are cleanly divided into different sections (being general, category, brands)
            this.addSuggestionsInSeparateSections(matchingRecentSearchSuggestions, suggestionExists, suggestions);
        } else {
            // This is the old design where suggestions of different nature were mixed in one overall suggestions section
            this.addSuggestionsInOverallSection(matchingRecentSearchSuggestions, suggestionExists, suggestions);
        }
    }

    getSuggestionSections() {
        const suggestions = [];
        const firstValue = this.props.value;
        const recentSearchSuggestions = loadRecentSearches();
        if (recentSearchSuggestions && !firstValue) {
            const mostRecentSearchSuggestions = recentSearchSuggestions.slice(0, 3);
            suggestions.push({
                title: "AUTOSUGGEST_PREVIOUSLY_SEARCHED_SECTION_TITLE",
                suggestions: mostRecentSearchSuggestions
            });
        }

        if (firstValue) {
            this.constructUserInputDependentSections(suggestions);
        }

        const currentFavourites = findSuggestions(firstValue, this.props.favouritesMap || {}) || [];
        if (firstValue && currentFavourites.length > 0) {
            suggestions.push({
                title: "AUTOSUGGEST_ARTICLE_SUGGESTIONS_SECTION_TITLE",
                suggestions: wrapFavourites(currentFavourites, this.props.favouriteDataMap)
            });
        }

        const links = this.getLinkSuggestions();
        if (links.length > 0) {
            suggestions.push({
                title: "AUTOSUGGEST_QUICK_LINKS",
                suggestions: links
            });
        }

        return suggestions;
    }

    render() {
        const theme = {
            suggestion: "list-group-item clickable",
            suggestionHighlighted: "active",
            suggestionsList: "list-group",
            suggestionsContainer: "suggestionsContainer",
            sectionTitle: "suggestion-section-title"
        };

        const suggestions = this.getSuggestionSections();

        const boundRenderer = (props: any) => this.props.renderInputComponent(props, this.onSubmit);
        const { inputProps } = this.props;
        return (
            <form onSubmit={this.onSubmit.bind(this)}>
                <FormGroup className="search-header-autosuggestion">
                    <ReactAutoSuggest
                        containerProps={{ "aria-label": inputProps.placeholder }}
                        id={"autosuggest-search-input" + (this.props.isMobile ? "-mobile" : "")}
                        theme={theme}
                        suggestions={suggestions}
                        shouldRenderSuggestions={() => true}
                        focusInputOnSuggestionClick={false}
                        multiSection
                        getSectionSuggestions={((section: any) => section.suggestions) as any}
                        renderSuggestion={this.renderSuggestionSafely}
                        renderSectionTitle={this.renderSectionTitle}
                        onSuggestionSelected={this.suggestionSelected as any}
                        onSuggestionsFetchRequested={this.loadSuggestions}
                        onSuggestionsClearRequested={() => {}}
                        getSuggestionValue={this.getSuggestionValue}
                        inputProps={inputProps}
                        renderInputComponent={boundRenderer}
                    />
                </FormGroup>
            </form>
        );
    }
}

export default connect(
    {
        stores: [
            [
                ArticleSuggestionStore,
                (s: typeof ArticleSuggestionStore) => ({
                    suggestionsMap: s.getSuggestionsMap(),
                    favouritesMap: s.getFavouritesMap(),
                    favouriteDataMap: s.getFavouriteDataMap()
                })
            ],
            [
                SessionStore,
                (s: typeof SessionStore) => ({
                    selectedStoreId: s.getCustomerStore(),
                    loggedIn: s.loggedIn()
                })
            ],
            [
                CartStore,
                (s: typeof CartStore) => ({ shoppingCartItemCount: s.getItemCount(), cartEnabled: s.isEnabled() })
            ],
            [CustomerStore, (store: typeof CustomerStore) => store.getCustomerStoreSelectorSettings()]
        ]
    },
    AutosuggestInput
);
